import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material'
import React, { useState, useEffect } from 'react'
import {
  PickingDetail,
  PickingGetRouteDetail,
  PickingInputData,
  PickingRoute,
  pickingRouteDetailMovement
} from '../../../interfaces/Picking/Picking'
import { apiCall, ApiConfig } from '../../../config/api'
import { PickingSkuDetail } from './PickingSkuDetail'
import { PickingRouteDetail } from './PickingRouteDetail'
import HelpIcon from '@mui/icons-material/Help'
import { NewPickingRouteHeader } from './NewPickingRouteHeader'
import AddBoxIcon from '@mui/icons-material/AddBox'
import ErrorIcon from '@mui/icons-material/Error'

interface PickingFinishedProps {
  pickingRoute?: PickingRoute
  handleFinishRoute: (inputData: PickingInputData) => void
  handleLPNButton: () => void
  requiredTote: boolean
}

const detailsInitialState: PickingGetRouteDetail = {
  pickingRouteId: 16,
  userId: 2,
  startDate: '2021-06-30T15:42:14.8623131',
  totalSku: 1,
  realQty: 1,
  pickedQty: 1,
  workOrderId: [5],
  pickingRouteDetailMovement: [
    {
      qtyPicked: 1,
      positionId: 504,
      positionName: 'X.Verde',
      sku: '',
      batch: ''
    }
  ],
  packingPositions: [],
  relatedRouteIds: []
}

export const PickingFinished: React.FC<PickingFinishedProps> = ({
  pickingRoute,
  handleFinishRoute,
  handleLPNButton,
  requiredTote
}) => {
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('sm'))
  const initialInput: PickingInputData = {
    sku: '',
    quantity: 0,
    positionName: ''
  }
  const [isDetail, setIsDetail] = useState(false)
  const [inputData, setInputData] = useState<PickingInputData>(initialInput)
  const [formValues, setValues] = useState(initialInput)
  const [formErrors, setErrors] = useState({} as any)
  const handleClickResumeDetail = () => setIsDetail(!isDetail)
  const [details, setDetails] =
    useState<PickingGetRouteDetail>(detailsInitialState)
  const [pickingDetail, setPickingDetail] = useState<PickingDetail[]>([
    { position: '', sku: '', qty: 0, batch: '' }
  ])
  const [showHelp, setShowHelp] = useState<boolean>(false)

  useEffect(() => {
    apiCall(
      'GET',
      `${ApiConfig.PickingRouteApi}/GetPickingDetail?pickingRouteId=${pickingRoute?.pickingRouteId}`
    ).then(({ data: { data } }) => {
      setDetails(data)
      setPickingDetail(
        data.pickingRouteDetailMovement.map(
          (mov: pickingRouteDetailMovement) => ({
            position: mov.positionName,
            qty: mov.qtyPicked,
            sku: mov.sku,
            batch: mov.batch
          })
        )
      )
    })
  }, [])

  const validateInput: any = (name: string, value: string) => {
    const newErrors: any = { ...formErrors }

    if (name === 'positionName') {
      delete newErrors['positionName']
      newErrors.positionName = value ? '' : 'Campo requerido'
    }

    setErrors({
      ...newErrors
    })
  }

  const handleInputValue = (e: any) => {
    const name = e.target.name
    const value = e.target.value
    setValues({
      ...formValues,
      [name]: value
    })
    validateInput(name, value)
    setInputData({ ...inputData, [name]: value })
  }

  const formIsValid = () => {
    const isValid =
      formValues.positionName &&
      Object.values(formErrors).every((x) => x === '')

    return isValid
  }

  const handleFormSubmit = () => {
    const isValid = formIsValid()
    if (isValid) {
      handleFinishRoute(inputData)
    }
  }

  const packingHelp = (
    <div style={{ textAlign: 'center' }}>
      <Typography variant='subtitle2'>
        Cajas disponibles para vaciado de productos:
      </Typography>
      <ul>
        {details.packingPositions.map((pos) => (
          <li key={pos.positionId}>
            <Typography variant='body2'>{pos.positionName}</Typography>{' '}
          </li>
        ))}
      </ul>
    </div>
  )

  return (
    <Box
      display='flex'
      flex='1'
      flexDirection='column'
      justifyContent='space-around'
    >
      <NewPickingRouteHeader pickingRoute={pickingRoute} />

      <Box padding='5px' textAlign='center' color='green'>
        <Typography variant='h4' display='block' gutterBottom>
          Picking Finalizado
        </Typography>
      </Box>

      {details.relatedRouteIds && details.relatedRouteIds.length > 0 && (
        <Grid
          container
          direction='column'
          justifyContent='center'
          alignItems='center'
          spacing={2}
        >
          <Grid item>
            <Box
              display='inline-flex'
              bgcolor='#FFF3CD'
              border='1px solid #FFEEBA'
              borderRadius={8}
              padding='6px 10px'
              alignItems='center'
              textAlign='center'
              style={{ width: 'auto' }}
            >
              <ErrorIcon
                style={{ color: '#856404', marginRight: 8, fontSize: 20 }}
              />
              <Typography
                variant='body2'
                style={{
                  color: '#856404',
                  fontWeight: 500,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  textAlign: 'center'
                }}
              >
                Esta ruta está asociada a otra: (#
                {details.relatedRouteIds.join(', #')})
              </Typography>
            </Box>
          </Grid>
        </Grid>
      )}

      <Box padding='5px' textAlign='center'>
        <Typography variant='caption' display='block' gutterBottom>
          Favor deje los productos en el sector de validación
        </Typography>
      </Box>

      <Box
        component='form'
        alignItems='center'
        justifySelf='center'
        justifyContent='center'
        justifyItems='center'
      >
        <Grid
          container
          item
          xs={12}
          sm={12}
          md={7}
          justifyContent='flex-end'
          alignItems='flex-end'
        >
          <Grid item xs={7} sm={7} md={3}>
            <TextField
              id='positionName'
              name='positionName'
              label='Ubicación'
              onChange={handleInputValue}
              autoFocus
              autoComplete='off'
            />
          </Grid>
          <Grid item xs={4} sm={4} md={2}>
            <Button
              type='submit'
              onClick={handleFormSubmit}
              variant='contained'
              color='primary'
              style={{ marginLeft: 10 }}
              disabled={!formIsValid()}
            >
              {'>'}
            </Button>
          </Grid>
          <Grid item xs={1} alignContent='center'>
            <Tooltip title={packingHelp} placement='top-end'>
              <IconButton onClick={() => setShowHelp(!showHelp)}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {matches &&
            (requiredTote ? (
              <Box padding={'15px'} textAlign={'center'}>
                <Typography variant='subtitle2' style={{ textAlign: 'center' }}>
                  {`LPN/Tote: ${pickingRoute?.lpn || ''} `}
                </Typography>
              </Box>
            ) : (
              <Box padding={'15px'}>
                <Button
                  fullWidth
                  variant='contained'
                  color='primary'
                  endIcon={<AddBoxIcon />}
                  onClick={handleLPNButton}
                >
                  LPN
                </Button>
                <Box padding={'15px'}>
                  <Typography
                    variant='subtitle2'
                    style={{ textAlign: 'center' }}
                  >
                    {pickingRoute?.lpn || 'LPN no registrada'}
                  </Typography>
                </Box>
              </Box>
            ))}
        </Grid>
      </Box>

      {showHelp && (
        <Box padding='15px'>
          <div
            style={{
              padding: '20px',
              backgroundColor: '#FBF1E6',
              color: 'black'
            }}
          >
            {packingHelp}
          </div>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {' '}
          <Box padding='15px'>
            <Button
              variant='contained'
              color='primary'
              onClick={handleClickResumeDetail}
              fullWidth
            >
              {isDetail ? 'Resumen' : 'Detalle Ruta'}
            </Button>{' '}
          </Box>
          <Grid item xs={12}>
            {isDetail ? (
              <PickingSkuDetail pickingDetailList={pickingDetail} />
            ) : (
              <PickingRouteDetail
                sku={details.totalSku.toString()}
                ot={details.workOrderId.join('-')}
                qty={`${details.pickedQty} / ${details.realQty}`}
              />
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          {!matches &&
            (requiredTote ? (
              <Box padding={'15px'} textAlign={'center'}>
                <Typography variant='subtitle2' style={{ textAlign: 'center' }}>
                  {`LPN/Tote: ${pickingRoute?.lpn || ''} `}
                </Typography>
              </Box>
            ) : (
              <Box padding={'15px'} textAlign={'center'}>
                <Button
                  fullWidth
                  variant='contained'
                  color='primary'
                  endIcon={<AddBoxIcon />}
                  onClick={handleLPNButton}
                >
                  LPN
                </Button>
                <Box padding={'15px'}>
                  <Typography
                    variant='subtitle2'
                    style={{ textAlign: 'center' }}
                  >
                    {pickingRoute?.lpn || 'LPN no registrada'}
                  </Typography>
                </Box>
              </Box>
            ))}
        </Grid>
      </Grid>
    </Box>
  )
}
